import { useContext, useState } from 'react';
import { DEFAULT_CONTROL_VARIANT } from '@tn/abtests';
import { ABTestExperimentsContext } from '@tn/abtests/context/ABTestExperimentsContext';
import { Banner, Body, Button, ButtonProps, Container, Heading, Stack } from '@vds/components';
import { getCookie, hasCookie, setCookie } from 'cookies-next';

import { appLogger } from '@/utils/app-logger/appLogger';
import { FORWARD_SLASH, getLoadedVariantsCookieName } from '@/utils/constants/common';

type AbTestExperimentsState = {
    notification: string | JSX.Element;
};

const primaryButtonProps = {
    colorScheme: 'primary',
    size: 'medium',
    variant: 'solid',
} as ButtonProps;

const secondaryButtonProps = {
    colorScheme: 'secondary',
    size: 'medium',
    variant: 'solid',
} as ButtonProps;

function getCookieArr(cookieName: string) {
    let cookieObj: Record<string, string> = {};
    if (hasCookie(cookieName)) {
        const cookieValue = getCookie(cookieName) as string;
        try {
            const decoded = JSON.parse(cookieValue);
            if (decoded && typeof decoded === 'object') {
                cookieObj = decoded;
            }
        } catch (err) {
            appLogger.warn(`AbTestExperiments: malformed ${cookieName} cookie ignored`, {
                cookieName,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    return cookieObj;
}

// convert snake_case to Snake Case for better readability in the UI
function formatExperimentName(experimentKey: string) {
    if (!experimentKey) return experimentKey;
    return experimentKey
        .replace(/_tn_/gi, ' TN ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\s+/, ' ')
        .trim();
}

export default function AbTestExperiments() {
    // Detect the provider without triggering the throwing hook. When the drawer
    // is mounted somewhere the provider hasn't wrapped yet (e.g. dev builds of
    // signup during the CBSCOMSUB-11180/-11181 split), silently no-op instead
    // of crashing the page.
    const context = useContext(ABTestExperimentsContext);
    const [state, setState] = useState<AbTestExperimentsState>({
        notification: '',
    });
    if (!context) return null;
    const { userId } = context.state;
    const experimentsList = [...context.state.experimentsListForUiTool];

    function updateCookie(
        experimentKey: string,
        variant: string,
        userId: string | null | undefined,
    ) {
        if (!userId) {
            return;
        }

        const cookieExpiration = 24 * 60 * 60;
        const cookieObj: Record<string, string> = getCookieArr(getLoadedVariantsCookieName(userId));
        cookieObj[experimentKey] = variant;

        setCookie(getLoadedVariantsCookieName(userId), cookieObj, {
            maxAge: cookieExpiration,
            path: FORWARD_SLASH,
        });

        const notification = (
            <>
                <div>
                    Successfully updated <strong>{experimentKey}</strong> to{' '}
                    <strong>{variant}</strong>. Reloading the page to take effect.
                </div>
            </>
        );

        // The setState below queues a success banner that flashes briefly before the
        // reload navigates away. This is intentional — dev/QA use this repeatedly and
        // waiting on a banner to be readable is worse UX than getting to the target
        // page immediately. The reloaded page is the real confirmation.
        setState((prevState) => {
            return {
                ...prevState,
                notification,
            };
        });

        window.location.reload();
    }

    const handleButtonClick = (
        experimentKey: string,
        variant: string,
        userId: string | null | undefined,
    ) => {
        updateCookie(experimentKey, variant, userId);
    };

    return (
        <div>
            {state.notification && (
                <Banner
                    autoHideDuration={3000}
                    isDismissable
                    dismissLabel="Close banner"
                    hasNavContainer
                    variant="success"
                    transitionDuration={0.3}>
                    {state.notification}
                </Banner>
            )}

            <div
                style={{
                    padding: 'var(--spacing-4)',
                }}>
                ProfileId / Ovvuid: {userId}
            </div>

            <div
                style={{
                    padding: 'var(--spacing-4)',
                }}>
                <Stack gap="spacing-2" flexDirection="row" justifyContent="center">
                    <Button {...primaryButtonProps}>Selected Variant</Button>
                    <Button {...secondaryButtonProps}>Available Variants</Button>
                </Stack>
            </div>

            {experimentsList.length === 0 && (
                <div>Loading experiments or no experiments on this page.</div>
            )}

            <Stack gap="spacing-3" flexDirection="column">
                {experimentsList
                    .sort((a, b) => a.experimentKey.localeCompare(b.experimentKey))
                    .map((experiment) => {
                        const { experimentKey, variantsList, userVariant, userId, isTracked } =
                            experiment;
                        const isRunning = Boolean(isTracked);
                        return (
                            <div
                                key={experimentKey}
                                style={{
                                    padding: 'var(--spacing-4)',
                                    backgroundColor: isRunning
                                        ? 'var(--color-bg-success)'
                                        : 'var(--color-bg-shades)',
                                }}>
                                <Container gap="spacing-3" flexDirection="column">
                                    <Heading size="small" color="primary" weight="bold">
                                        {formatExperimentName(experimentKey)}
                                    </Heading>

                                    <Container flexDirection="row" alignItems="baseline">
                                        <Container flexDirection="column" alignItems="baseline">
                                            <Body
                                                color="secondary"
                                                size="small"
                                                variant="metadata"
                                                weight="bold">
                                                Experiment Key
                                            </Body>
                                            <Body
                                                color="primary"
                                                size="large"
                                                variant="body-02"
                                                weight="bold">
                                                {experimentKey}
                                            </Body>
                                        </Container>
                                    </Container>
                                    <Container flexDirection="column" alignItems="baseline">
                                        <Body
                                            color="secondary"
                                            size="small"
                                            variant="metadata"
                                            weight="bold">
                                            Switch Variant To
                                        </Body>
                                    </Container>
                                    {/* Variants Switcher */}
                                    <Container gap="spacing-3">
                                        <Stack gap="spacing-2" flexDirection="row">
                                            {[...variantsList]
                                                .sort((a, b) => {
                                                    if (a === DEFAULT_CONTROL_VARIANT) return -1;
                                                    if (b === DEFAULT_CONTROL_VARIANT) return 1;
                                                    return a.localeCompare(b);
                                                })
                                                .map((variant) => {
                                                    const buttonProps =
                                                        variant === userVariant
                                                            ? primaryButtonProps
                                                            : secondaryButtonProps;
                                                    return (
                                                        <Button
                                                            {...buttonProps}
                                                            key={variant}
                                                            disabled={!isRunning}
                                                            onClick={() => {
                                                                handleButtonClick(
                                                                    experimentKey,
                                                                    variant,
                                                                    userId,
                                                                );
                                                            }}>
                                                            {variant}
                                                        </Button>
                                                    );
                                                })}
                                        </Stack>
                                    </Container>
                                </Container>
                            </div>
                        );
                    })}
            </Stack>
        </div>
    );
}
